import { Injectable, signal, inject } from '@angular/core';
import { Base64 } from 'js-base64';
import { StorageService } from './storage.service';
import { HttpClient } from '@angular/common/http';
import { Observable, of, timer } from 'rxjs';
import { catchError, tap, map, retry } from 'rxjs/operators';
import { App_Routes } from '../enums/router.enum';
import { GLOBAL_CONSTANT } from '../constants/global.constant';
import { LP_UI_CONFIG } from '../core/tokens';
// Extend Window interface to include adobeDataLayer
declare global {
  interface Window {
    adobeDataLayer?: {
      userAccount: { loginStatus: string };
      _henkel?: { page: { language: string } };
    }[];
  }
}

interface AuthResponse {
  user: string;
  js_token: string;
}

type LoginCallback = (username?: string) => void;

/**
 * ✅ AuthenticationService: Handles authentication operations
 *
 * NOTE: Authorization headers are added automatically by authInterceptor
 * No need to manually add headers in HTTP calls (except for login endpoints)
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly cfg = inject(LP_UI_CONFIG);
  // OAuth token settings
  readonly tokenSettings = {
    response_type: 'token id_token',
    scope: 'openid',
    claims: 'com pos loc fnm lnm dob',
    state: 'xyz',
  };

  private loginCallbacks = signal<LoginCallback[]>([]);
  public returnUrl = signal<string | undefined>('');

  private storage = inject(StorageService);
  private http = inject(HttpClient);

  /**
   * Generate nonce for OAuth
   */
  private getNonce(): string {
    return crypto.randomUUID();
  }

  /**
   * Build app host URL for email redirects
   */
  private buildAppHost(): string {
    const temp = window.location.href.split(App_Routes.FORWARD_SLASH);
    const baseTags = document.getElementsByTagName('base');
    const basePath = baseTags.length
      ? baseTags[GLOBAL_CONSTANT.DEFAULT_INITIALIZER].href.substr(
          location.origin.length,
          GLOBAL_CONSTANT.NINE_HUNDRED_NINETY_NINE
        )
      : GLOBAL_CONSTANT.EMPTY_STRING;
    let host =
      temp[GLOBAL_CONSTANT.DEFAULT_INITIALIZER] +
      App_Routes.FORWARD_DOUBLE_SLASH +
      temp[GLOBAL_CONSTANT.TWO] +
      basePath;
    if (host.lastIndexOf(App_Routes.FORWARD_SLASH) === host.length - GLOBAL_CONSTANT.ONE) {
      host = host.slice(GLOBAL_CONSTANT.DEFAULT_INITIALIZER, host.length - GLOBAL_CONSTANT.ONE);
    }
    return host;
  }

  /**
   * Update Adobe Data Layer login status
   */
  private updateAdobeLoginStatus(status: 'logged in' | 'logged out'): void {
    if (window.adobeDataLayer?.[0]) {
      window.adobeDataLayer[0].userAccount.loginStatus = status;
    }
  }

  /**
   * Handle successful login response
   */
  private handleLoginResponse(response: AuthResponse, overwriteUsername?: string): void {
    const username = overwriteUsername || response.user;
    if (username && response.js_token) {
      this.storage.setUserName(username);
      this.storage.setToken(response.js_token);
      this.updateAdobeLoginStatus('logged in');
      this.executeCallbacks(username);
    }
  }

  /**
   * Login via OAuth provider redirect
   */
  loginAtProvider(redirect?: string): Promise<null> {
    const url =
      this.cfg.authorization_uri +
      '&response_type=' +
      this.tokenSettings.response_type +
      '&client_id=' +
      this.cfg.client_id +
      '&scope=' +
      this.tokenSettings.scope +
      '&claims=' +
      this.tokenSettings.claims +
      '&state=' +
      this.tokenSettings.state +
      '&nonce=' +
      this.getNonce() +
      '&redirect_uri=' +
      this.cfg.redirect_uri;

    if (redirect) {
      this.returnUrl.set(redirect);
    }

    window.location.href = url;
    return Promise.resolve(null);
  }

  /**
   * Login with username and password (Basic Auth)
   * Manual auth header required because interceptor skips /login
   */
  loginWithCredentials(
    credentials: { user: string; pw: string; captchaResponse?: string },
    overwriteUsername?: string
  ): Observable<AuthResponse> {
    const authHeader =
      'Basic ' +
      Base64.encode(
        Base64.encode(credentials.user) + GLOBAL_CONSTANT.COLON + Base64.encode(credentials.pw)
      );
    if (this.cfg.enableCaptcha && credentials.captchaResponse) {
      return this.http
        .post<AuthResponse>(
          this.cfg.host + App_Routes.LOGIN_WITH_CREDENTIALS_PATH,
          {},
          {
            headers: {
              Authorization: authHeader,
              'X-Captcha-Response': credentials.captchaResponse,
            },
            responseType: 'json',
          }
        )
        .pipe(
          tap((response) => this.handleLoginResponse(response, overwriteUsername)),
          catchError((error) => {
            console.error('Login failed:', error);
            throw error;
          })
        );
    } else {
      return this.http
        .post<AuthResponse>(
          this.cfg.host + App_Routes.LOGIN_WITH_CREDENTIALS_PATH,
          {},
          {
            headers: { Authorization: authHeader },
            responseType: 'json',
          }
        )
        .pipe(
          tap((response) => this.handleLoginResponse(response, overwriteUsername)),
          catchError((error) => {
            console.error('Login failed:', error);
            throw error;
          })
        );
    }
  }

  /**
   * Login with access token (for OAuth flow)
   * Auth header added automatically by interceptor
   */
  loginWithAccessToken(
    parameters: Record<string, unknown>,
    overwriteUsername?: string
  ): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(this.cfg.host + App_Routes.AUTHORIZE_ACCESS_TOKEN_PATH, parameters)
      .pipe(
        map((response) => {
          // Handle string-wrapped responses
          let parsed = response;
          while (typeof parsed === 'string') {
            parsed = JSON.parse(parsed) as AuthResponse;
          }
          return parsed;
        }),
        tap((response) => this.handleLoginResponse(response, overwriteUsername)),
        catchError((error) => {
          console.error('Token login failed:', error);
          throw error;
        })
      );
  }

  /**
   * Login via MSAL (Microsoft Authentication Library)
   * Manual auth header required for MSAL token exchange
   */
  loginViaMsal(accessToken: string, convert = false): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(
        this.cfg.host + App_Routes.AUTHORIZE_MSAL_TOKEN_PATH,
        { ConvertToSSOUser: convert },
        { headers: { Authorization: 'Bearer ' + accessToken } }
      )
      .pipe(
        map((response) => {
          let parsed = response;
          while (typeof parsed === 'string') {
            parsed = JSON.parse(parsed) as AuthResponse;
          }
          return parsed;
        }),
        tap((response) => this.handleLoginResponse(response)),
        catchError((error) => {
          console.error('MSAL login failed:', error);
          throw error;
        })
      );
  }

  /**
   * Login via token with automatic retry on failure
   * Manual auth header required for token validation
   */
  loginViaToken(token: string): Observable<boolean> {
    const parameters = { js_token: token };

    return this.http
      .post<AuthResponse>(this.cfg.host + App_Routes.LOGIN_VIA_TOKEN_PATH, parameters, {
        headers: { Authorization: 'Bearer ' + token },
      })
      .pipe(
        retry({
          count: 5,
          delay: (error, retryCount) => {
            return timer(1000 * retryCount);
          },
        }),
        tap((response) => {
          this.storage.setUserName(response.user);
          this.storage.setToken(token);
          this.updateAdobeLoginStatus('logged in');
        }),
        map(() => true),
        catchError((_error) => {
          return of(false);
        })
      );
  }

  /**
   * Request password reset email
   */
  requestForgotPasswordMail(email: string, captchaResponse?: string): Observable<unknown> {
    const parameters = {
      email: email,
      app_id: this.cfg.AppID,
      appHost: this.buildAppHost(),
    };
    // Public endpoint - auth header added by interceptor
    if (this.cfg.enableCaptcha && captchaResponse) {
      return this.http
        .post(this.cfg.host + App_Routes.FORGOT_PW_PATH, parameters, {
          headers: { 'X-Captcha-Response': captchaResponse },
        })
        .pipe(
          catchError((error) => {
            console.error('Forgot password request failed:', error);
            throw error;
          })
        );
    } else {
      return this.http.post(this.cfg.host + App_Routes.FORGOT_PW_PATH, parameters).pipe(
        catchError((error) => {
          console.error('Forgot password request failed:', error);
          throw error;
        })
      );
    }
  }

  /**
   * Check if forgot password token is valid
   * Auth header added automatically by interceptor
   */
  checkForgotPasswordToken(token: string): Observable<boolean> {
    const parameters = {
      js_token: 'Bearer ' + token,
      scope: 'change-pw',
    };

    return this.http
      .post(this.cfg.host + App_Routes.CHECK_FORGOT_PW_TOKEN_PATH, parameters, {
        headers: { Authorization: 'Bearer ' + token },
      })
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  /**
   * Set new password via email token
   * Auth header added automatically by interceptor
   */
  setNewPasswordViaEmail(token: string, password: string): Observable<boolean> {
    const parameters = {
      js_token: 'Bearer ' + token,
      scope: 'change-pw',
      appHost: this.buildAppHost(),
      pw: password,
    };

    return this.http
      .post(this.cfg.host + App_Routes.CHANGE_PW_PATH, parameters, {
        headers: { Authorization: 'Bearer ' + token },
      })
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }

  /**
   * Change password from settings
   * Auth header added automatically by interceptor
   */
  setNewPasswordViaSettings(currentPw: string, newPw: string): Observable<boolean> {
    const parameters = {
      current_pw: currentPw,
      new_pw: newPw,
      appHost: this.buildAppHost(),
    };

    return this.http.post(this.cfg.host + App_Routes.SETTING_CHANGE_PW_PATH, parameters).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Check if user is currently logged in
   * Auth header added automatically by interceptor
   */
  checkLoginStatus(token: string | null = null): Observable<boolean> {
    if (token) {
      this.storage.setToken(token);
    }

    const parameters = {
      username: this.storage.userName(),
      js_token: 'Bearer ' + this.storage.token(),
      scope: 'db-access',
    };

    return this.http.post(this.cfg.host + App_Routes.LOGIN_VIA_TOKEN_PATH, parameters).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Check if user has valid token
   */
  isLoggedIn(): boolean {
    try {
      const token = this.storage.token();
      const isValid = !!token && token.length > 0;
      this.updateAdobeLoginStatus(isValid ? 'logged in' : 'logged out');
      return isValid;
    } catch {
      this.updateAdobeLoginStatus('logged out');
      return false;
    }
  }

  /**
   * Logout user and clear session
   */
  logout(callback?: () => void, returnUrl?: string): void {
    this.updateAdobeLoginStatus('logged out');
    this.returnUrl?.set(returnUrl);
    this.storage.clearToken();

    if (callback) {
      callback();
    }
  }

  /**
   * User signup
   */
  signup(
    formValues: {
      email: string;
      name: string;
      surname: string;
      customerentity: string;
      title: string;
      country: string;
      password: string;
      captchaResponse?: string;
    },
    lang: string
  ): Observable<boolean> {
    const parameters = {
      email: formValues.email,
      name: formValues.name,
      surname: formValues.surname,
      customer: formValues.customerentity,
      designation: formValues.title,
      country: formValues.country,
      password: formValues.password,
      lang,
      appHost: this.buildAppHost(),
    };

    const captchaResponse = formValues.captchaResponse ? formValues.captchaResponse : '';

    // Public endpoint - no auth required
    if (this.cfg.enableCaptcha && (captchaResponse !== '' || captchaResponse != null)) {
      return this.http
        .post(this.cfg.host + App_Routes.SIGNUP_PATH, parameters, {
          headers: { 'X-Captcha-Response': captchaResponse },
        })
        .pipe(
          map(() => true),
          catchError(() => of(false))
        );
    } else {
      return this.http.post(this.cfg.host + App_Routes.SIGNUP_PATH, parameters).pipe(
        map(() => true),
        catchError(() => of(false))
      );
    }
  }

  /**
   * Process registration request (admin approval)
   * Manual auth header required as it uses custom token
   */
  processRegistrationRequest(
    accessGranted: boolean,
    customer: number,
    token: string,
    isAccessToIA: boolean,
    reviewComments: string
  ): Observable<unknown> {
    return this.http.post(
      this.cfg.host + App_Routes.PROCESS_REGISTRATION_PATH,
      {
        access_granted: accessGranted.toString(),
        customer: customer.toString(),
        appHost: this.buildAppHost(),
        IsAccessToIA: isAccessToIA.toString(),
        review_comments: reviewComments,
      },
      { headers: { Authorization: 'Bearer ' + token } }
    );
  }

  /**
   * Register a callback to be executed on successful login
   */
  registerLoginCallback(callback: LoginCallback): number {
    const updated = this.loginCallbacks().concat(callback);
    this.loginCallbacks.set(updated);
    return updated.length - 1;
  }

  /**
   * Unregister a login callback
   */
  unregisterLoginCallback(ref: number): void {
    if (ref >= 0 && ref < this.loginCallbacks().length) {
      const callbacks = [...this.loginCallbacks()];
      callbacks[ref] = () => {
        // No-op callback
      };
      this.loginCallbacks.set(callbacks);
    }
  }

  /**
   * Execute all registered login callbacks
   */
  private executeCallbacks(username: string): void {
    this.loginCallbacks().forEach((callback) => {
      if (callback) {
        setTimeout(() => callback(username), 1);
      }
    });
  }

  // Harshit move this API as per the code structure
  public loadUserApprovalData(token: string): Observable<any> {
    // uses special registration auth token containing the registration id, no user login required
    return this.http.get(this.cfg.host + 'api/auth/approvaldata', {
      headers: { Authorization: 'Bearer ' + token },
    });
  }
}
